/**
 * CrmModuleShell — the shared layout for the cm-api-backed modules (CRM, Hotel
 * Supplier Management, Notebooks). Mirrors the Expense surface: a page title +
 * an in-page pill-tab bar ACROSS THE TOP (secondary nav; the shell rail is
 * primary). No in-module sidebar and no nested rail items.
 *
 * It provides the CRM styling boundary (.crm-scope) + the MeProvider capability
 * context, then renders capability-gated tabs + the active page in <Outlet>.
 * Tab selection drives the route so deep links work — same pattern as
 * ExpenseLayout.
 *
 * PER-PAGE RBAC: a tab with a `capability` is hidden unless the user's role
 * confers it (useMe().can). While /me is still loading we show all tabs, then
 * hide the unauthorized ones — the SAME affordance the old CRM sidebar used.
 * Server-side enforcement (cm-api deny-by-default) remains the real boundary.
 *
 * A module with 0–1 visible tabs renders no tab bar (e.g. Notebooks is a single
 * page) — just the title + content.
 */
import { Tabs, TabsList, TabsTrigger } from '@andavo/web-ui';
import { Outlet, useLocation, useNavigate } from 'react-router';
import type { Capability } from './auth/roles';
import { MeProvider, useMe } from './auth/use-capabilities';
import './crm-styles.css';

export interface ModuleTab {
  value: string;
  text: string;
  path: string;
  /** Authority required to see this tab; undefined = always visible. */
  capability?: Capability;
}

export interface CrmModuleShellProps {
  /** Module title shown above the tabs. Omit when the page renders its own header
   *  (e.g. Notebooks) to avoid a duplicate heading. */
  title?: string;
  subtitle?: string;
  tabs: ModuleTab[];
}

export function CrmModuleShell(props: CrmModuleShellProps) {
  return (
    <MeProvider>
      <div className="crm-scope">
        <ModuleInner {...props} />
      </div>
    </MeProvider>
  );
}

function ModuleInner({ title, subtitle, tabs }: CrmModuleShellProps) {
  const { me, can } = useMe();
  const navigate = useNavigate();
  const location = useLocation();

  // Show all tabs while /me loads; then hide the ones the role can't see.
  const visible = tabs.filter((t) => !t.capability || !me || can(t.capability));

  const activeIndex = Math.max(
    0,
    visible.findIndex((t) => location.pathname.startsWith(t.path)),
  );
  const activeValue = visible[activeIndex]?.value ?? visible[0]?.value ?? '';

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      {title || subtitle ? (
        <div className="flex flex-col gap-1">
          {title ? (
            <h1 className="font-sans font-semibold text-2xl text-content-primary">
              {title}
            </h1>
          ) : null}
          {subtitle ? (
            <p className="font-inter text-content-secondary text-sm">
              {subtitle}
            </p>
          ) : null}
        </div>
      ) : null}

      {visible.length > 1 ? (
        <Tabs
          value={activeValue}
          onValueChange={(value) => {
            const next = visible.find((t) => t.value === value);
            if (next) navigate(next.path);
          }}
        >
          <TabsList initSelectedIndex={activeIndex}>
            {visible.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.text}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      ) : null}

      <Outlet />
    </div>
  );
}
